import { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import * as S from './Team.styles';
import { CDN_BASE_URL } from '../../config';
import { getSiteContent } from '../../api/siteContentApi';

// 导入 Swiper 样式
import 'swiper/css';

// 默认领导层数据 (fallback)
const DEFAULT_LEADERSHIP = [
  {
    id: 'lucy',
    name: 'Lucy Lu',
    initials: 'LL',
    title: 'Director of Uplus Education',
    image: 'lucy.jpg',
    campus: 'Victoria Park',
    achievements: [
      'Started education profession since 2008',
      'TRBWA registered qualified teacher for Secondary Humanities & Social Science and Mathematics',
      'Graduated with a master\'s degree in Secondary Education from Curtin',
      'Graduated with a bachelor\'s degree in Commerce from UWA',
      'Had teaching experience in multiple leading Western Australian high schools',
      'Awarded Outstanding Australian Education and Teaching Contribution Award by ACWF in 2021',
      'Awarded scholarships by Department of Education WA for studies in Year 11 and Year 12 in 2006'
    ]
  },
  {
    id: 'michael',
    name: 'Michael Kuai',
    initials: 'MK',
    title: 'CAMPUS MANAGER',
    image: 'michael.jpg',
    campus: 'Claremont',
    achievements: [
      'Manager of Uplus Education Claremont Campus',
      'IT Education Manager overseeing tech-integrated curriculum and digital systems',
      'Started STEM education profession since 2020, graduated with a master\'s degree in Cyber Security',
      'Partners with Perth schools and child care centers for STEM programs',
      'Former course developer for DJI\'s education department',
      'Awarded 4th place in Asia-Pacific Lego robotics competition, 2016'
    ]
  },
  {
    id: 'simon',
    name: 'Simon Liu',
    initials: 'SL',
    title: 'EDUCATOR',
    image: 'simon.jpg',
    campus: 'Victoria Park',
    achievements: [
      'A highly qualified educator with a strong academic background and a passion for teaching',
      'Completed both undergraduate and postgraduate studies in Physiotherapy at Curtin University, where entry into the program required an ATAR above 98',
      'Named to the Curtin University Vice Chancellor\'s List in 2002, an honour reserved for students achieving results in the top 1% across the university'
    ]
  }
];

// 动态生成教师数据 - 01.webp 到 37.webp
const allTeachers = Array.from({ length: 37 }, (_, index) => ({
  id: index + 1,
  image: `${String(index + 1).padStart(2, '0')}.webp`
}));

const Team = () => {
  const [leadership, setLeadership] = useState(DEFAULT_LEADERSHIP);
  const [loadedImages, setLoadedImages] = useState({});
  const [errorImages, setErrorImages] = useState({});
  const [allSlides, setAllSlides] = useState([]);
  const [slidesPerView, setSlidesPerView] = useState(4);
  const [usedTeacherIds, setUsedTeacherIds] = useState(new Set());
  const swiperRef = useRef(null);

  // Load custom leadership data from API
  useEffect(() => {
    getSiteContent('team_leaders').then(data => {
      if (data) setLeadership(data);
    });
  }, []);

  // 获取当前屏幕对应的每页显示数量
  const getCurrentSlidesPerView = useCallback(() => {
    const width = window.innerWidth;
    if (width >= 1200) return 7;
    if (width >= 768) return 5;
    return 4;
  }, []);

  // 生成不与已使用ID重复的随机教师页面
  const generateRandomPage = useCallback((count, excludeIds = new Set()) => {
    const availableTeachers = allTeachers.filter(teacher => !excludeIds.has(teacher.id));
    
    // 如果可用教师数量不足，重置排除列表
    if (availableTeachers.length < count) {
      const resetAvailable = [...allTeachers];
      return resetAvailable
        .sort(() => Math.random() - 0.5)
        .slice(0, count);
    }
    
    // 随机选择指定数量的教师
    return availableTeachers
      .sort(() => Math.random() - 0.5)
      .slice(0, count);
  }, []);

  // 生成多页幻灯片数据
  const generateMultiplePages = useCallback((slidesPerView) => {
    const pages = [];
    let currentUsedIds = new Set();
    
    // 生成足够的页面来支持smooth滑动 (至少3页)
    for (let i = 0; i < 5; i++) {
      const page = generateRandomPage(slidesPerView, currentUsedIds);
      pages.push(...page);
      
      // 更新已使用的ID - 只保留前一页的ID以避免重复
      if (i > 0) {
        currentUsedIds.clear();
      }
      page.forEach(teacher => currentUsedIds.add(teacher.id));
    }
    
    return pages;
  }, [generateRandomPage]);

  // 初始化和响应式更新
  useEffect(() => {
    const updateSlidesPerView = () => {
      const newSlidesPerView = getCurrentSlidesPerView();
      setSlidesPerView(newSlidesPerView);
      
      // 生成多页数据
      const slides = generateMultiplePages(newSlidesPerView);
      setAllSlides(slides);
    };

    updateSlidesPerView();
    window.addEventListener('resize', updateSlidesPerView);
    return () => window.removeEventListener('resize', updateSlidesPerView);
  }, [getCurrentSlidesPerView, generateMultiplePages]);

  // 当滑动到接近末尾时，添加新的页面
  const handleSlideChange = useCallback((swiper) => {
    const { activeIndex, slides } = swiper;
    const totalSlides = slides.length;
    
    // 当滑动到倒数第二页时，添加新的页面
    if (activeIndex >= totalSlides - slidesPerView * 2) {
      const newPage = generateRandomPage(slidesPerView, usedTeacherIds);
      setAllSlides(prev => [...prev, ...newPage]);
      
      // 更新已使用的ID集合
      setUsedTeacherIds(new Set(newPage.map(teacher => teacher.id)));
    }
  }, [slidesPerView, usedTeacherIds, generateRandomPage]);

  const handleImageLoad = (imageUrl) => {
    setLoadedImages(prev => ({ ...prev, [imageUrl]: true }));
  };

  const handleImageError = (imageUrl) => {
    setErrorImages(prev => ({ ...prev, [imageUrl]: true }));
  };

  // 获取领导层图片URL
  const getLeadershipImageUrl = (imageName) => {
    if (!imageName) return '';
    return `${CDN_BASE_URL}/static/tutor/leadership/${imageName}`;
  };

  // 获取教师图片URL
  const getTutorImageUrl = (imageName) => {
    return `${CDN_BASE_URL}/static/tutor/tutor/${imageName}`;
  };

  return (
    <S.PageContainer>
      <Helmet>
        <title>Uplus Education - Team</title>
        <meta name="description" content="Meet our experienced leadership team and talented tutors" />
      </Helmet>

      {/* Leadership - 使用相同的 MainLeaderCard 布局 */}
      {leadership.map((leader) => {
        const imageUrl = getLeadershipImageUrl(leader.image);
        return (
          <S.MainLeaderSection key={leader.id}>
            <S.MainLeaderCard>
              <S.MainLeaderAvatar>
                {leader.image && !errorImages[imageUrl] ? (
                  <img
                    src={imageUrl}
                    alt={leader.name}
                    onLoad={() => handleImageLoad(imageUrl)}
                    onError={() => handleImageError(imageUrl)}
                    style={{
                      opacity: loadedImages[imageUrl] ? 1 : 0,
                      transition: 'opacity 0.3s ease-in-out'
                    }}
                  />
                ) : (
                  leader.initials
                )}
              </S.MainLeaderAvatar>
              <S.MainLeaderInfo>
                <S.MainLeaderName>{leader.name}</S.MainLeaderName>
                <S.MainLeaderTitle>{leader.title}</S.MainLeaderTitle>
                <S.MainAchievementsList>
                  {leader.achievements.map((achievement, index) => (
                    <li key={index}>{achievement}</li>
                  ))}
                </S.MainAchievementsList>
              </S.MainLeaderInfo>
            </S.MainLeaderCard>
          </S.MainLeaderSection>
        );
      })}

      {/* 教师轮播图 */}
      <S.CarouselSection>
        <S.CarouselTitle>Our Teaching Team</S.CarouselTitle>
        <S.CarouselContainer>
          {allSlides.length > 0 ? (
            <Swiper
              ref={swiperRef}
              modules={[Autoplay]}
              slidesPerView={slidesPerView}
              slidesPerGroup={slidesPerView}
              spaceBetween={15}
              loop={false}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              onSlideChange={handleSlideChange}
              breakpoints={{
                480: { slidesPerView: 4, spaceBetween: 10, slidesPerGroup: 4 },
                768: { slidesPerView: 5, spaceBetween: 15, slidesPerGroup: 5 },
                1200: { slidesPerView: 7, spaceBetween: 15, slidesPerGroup: 7 },
              }}
            >
              {allSlides.map((teacher, index) => {
                const imageUrl = getTutorImageUrl(teacher.image);
                return (
                  <SwiperSlide key={`${teacher.id}-${index}`}>
                    <S.TeacherPhoto>
                      <img
                        src={imageUrl}
                        alt={`Teacher ${teacher.id}`}
                        onLoad={() => handleImageLoad(imageUrl)}
                        onError={() => handleImageError(imageUrl)}
                        style={{
                          opacity: loadedImages[imageUrl] ? 1 : 0,
                          transition: 'opacity 0.3s ease-in-out'
                        }}
                      />
                    </S.TeacherPhoto>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          ) : (
            <div>Loading teachers...</div>
          )}
        </S.CarouselContainer>

        {/* 团队统计信息 */}
        <S.TeamStats>
          Our team consists of <span>{50}+</span> dedicated educators
        </S.TeamStats>
      </S.CarouselSection>
    </S.PageContainer>
  );
};

export default Team;